import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, ShoppingBag, MessageCircle, BookOpen, Star } from "lucide-react";
import { WA_NUMBER } from "../mock";

const CENTER_INDEX = 2; // "Chat" lives in the raised center circle

const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const barRef = useRef(null);
  const [barWidth, setBarWidth] = useState(0);
  const [animX, setAnimX] = useState(0);
  const [animate, setAnimate] = useState(false);
  const [active, setActive] = useState(0);

  const items = [
    { label: "Beranda", icon: Home, section: "hero" },
    { label: "Produk", icon: ShoppingBag, section: "produk" },
    {
      label: "Chat",
      icon: MessageCircle,
      action: () =>
        window.open(
          `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
            "Halo ruangrasa! Saya mau bertanya tentang produk."
          )}`,
          "_blank"
        ),
    },
    { label: "Cerita Kami", icon: BookOpen, section: "cerita" },
    { label: "Testimoni", icon: Star, section: "testimoni" },
  ];

  const goSection = (section) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document
          .getElementById(section)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    } else {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const measure = () => setBarWidth(barRef.current?.offsetWidth || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Slide active icon from its slot into the center circle
  useEffect(() => {
    const slot = barWidth / 5;
    const fromX = (active - CENTER_INDEX) * slot;
    setAnimate(false);
    setAnimX(fromX);
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimate(true);
        setAnimX(0);
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [active, barWidth]);

  const handleClick = (item) => {
    setActive(items.indexOf(item));
    if (item.action) {
      item.action();
      return;
    }
    goSection(item.section);
  };

  const ActiveIcon = items[active].icon;
  const centerItem = items[CENTER_INDEX];

  // Build the 5 flat slots. The center slot is covered by the circle.
  // The active item is shown in the circle, so the gap it leaves is filled
  // by the center item.
  const slots = items.map((it, i) => {
    if (i === CENTER_INDEX) return null; // covered by circle
    if (i === active) return centerItem; // center item fills the empty gap
    return it;
  });

  return (
    <nav className="md:hidden fixed bottom-4 left-4 right-4 z-50">
      <div
        ref={barRef}
        className="relative bg-white rounded-full h-16 flex items-center shadow-xl shadow-[#004743]/15 border border-[#E3F1ED]"
      >
        {/* Raised center circle with the active icon */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 w-16 h-16 rounded-full bg-[#E3F1EC] flex items-center justify-center">
          <div
            className={`relative w-14 h-14 rounded-full bg-[#004743] flex items-center justify-center shadow-lg shadow-[#004743]/30 ${
              animate ? "transition-transform duration-500" : ""
            }`}
            style={{
              transform: `translateX(${animX}px)`,
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
              willChange: "transform",
            }}
          >
            <ActiveIcon className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Flat slots */}
        <div className="flex w-full">
          {slots.map((slotItem, i) => {
            if (!slotItem) {
              // center placeholder (under the circle)
              return <div key={`slot-${i}`} className="flex-1 h-16" />;
            }
            const Icon = slotItem.icon;
            return (
              <button
                key={slotItem.label}
                onClick={() => handleClick(slotItem)}
                className="flex-1 h-16 flex items-center justify-center"
                aria-label={slotItem.label}
              >
                <span className="transition-all duration-500 ease-out">
                  <Icon className="w-5 h-5 text-[#004743]/60" />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileNav;
